import { Badge, Button, Code, Group, Stack, Text, TextInput, Title } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";
import { api } from "../../api/client";
import { errorMessage } from "../../shared/session/errors";
import { clearOnboarding, onboardingHeaders, onboardingToken } from "../../shared/session/session";
import { SETUP_AUTHENTICATOR_PATH } from "../../shared/session/totp";
import { FormError, Notice, Page } from "../../shared/ui/Page";

export function OnboardingPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [otp, setOtp] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [issued, setIssued] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const token = onboardingToken();
  const state = useQuery({
    queryKey: ["onboarding", token],
    enabled: !!token && !issued,
    refetchInterval: (q) => (q.state.data?.code_issued ? false : 2000),
    queryFn: async () => {
      const { data, response } = await api.GET("/auth/onboarding", { headers: onboardingHeaders() });
      if (response.status === 401) throw new Error("expired");
      return data ?? null;
    },
    retry: false,
  });
  // The onboarding token is useless once the code is issued; forget it.
  useEffect(() => {
    if (issued) clearOnboarding();
  }, [issued]);
  if (issued) {
    return (
      <Page title={t("onboarding.code_title")}>
        <Code fz={32} ta="center" p="md" data-testid="pseudo-code">
          {issued}
        </Code>
        <Text>{t("onboarding.code_body")}</Text>
        <SignInChoice />
      </Page>
    );
  }

  if (!token) return <Navigate to="/onboarding/resume" replace />;
  if (state.error) {
    return (
      <Page title={t("onboarding.title")}>
        <Notice>{t("onboarding.expired")}</Notice>
        <Button component={Link} to="/onboarding/resume">
          {t("onboarding.resume_title")}
        </Button>
      </Page>
    );
  }
  const s = state.data;

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { data, error } = await api.POST("/auth/verify-phone", { headers: onboardingHeaders(), body: { otp } });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    if (data.pseudo_code) setIssued(data.pseudo_code);
    await qc.invalidateQueries({ queryKey: ["onboarding"] });
  };
  const resend = async () => {
    setError(null);
    const { error, response } = await api.POST("/auth/resend-verification", {
      headers: onboardingHeaders(),
      body: { channel: "sms" },
    });
    if (response.ok) setNotice(t("onboarding.resent"));
    else setError(errorMessage(error, t("error.generic")));
  };

  const identity = s?.verification_status ?? "pending";
  return (
    <Page title={t("onboarding.title")}>
      <Stack gap="xs">
        <Group justify="space-between">
          <Title order={2} size="h4">
            {t("onboarding.identity")}
          </Title>
          <Badge
            color={identity === "verified" ? "green" : identity === "rejected" ? "red" : "gray"}
            data-testid="identity-status"
          >
            {identity}
          </Badge>
        </Group>
        <Text size="sm" c="dimmed">
          {identity === "verified"
            ? t("onboarding.identity_verified")
            : identity === "rejected"
              ? t("onboarding.identity_rejected")
              : t("onboarding.identity_pending")}
        </Text>
        {identity === "rejected" && (
          <Button component={Link} to="/onboarding/retry" variant="light">
            {t("onboarding.retry")}
          </Button>
        )}
      </Stack>
      <Stack gap="xs">
        <Title order={2} size="h4">
          {t("onboarding.phone")}
        </Title>
        {s?.phone_verified ? (
          <Text data-testid="phone-verified">{t("onboarding.phone_verified")}</Text>
        ) : (
          <form onSubmit={verify}>
            <Text size="sm" mb="xs">
              {t("onboarding.phone_prompt", { phone: s?.masked_phone ?? "" })}
            </Text>
            <TextInput
              label={t("onboarding.otp")}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.currentTarget.value.trim())}
              required
            />
            <Group mt="sm">
              <Button type="submit" loading={busy}>
                {t("onboarding.verify")}
              </Button>
              <Button variant="subtle" onClick={resend}>
                {t("onboarding.resend")}
              </Button>
            </Group>
          </form>
        )}
      </Stack>
      <Stack gap="xs">
        <Title order={2} size="h4">
          {t("onboarding.email")}
        </Title>
        <Text size="sm">
          {s?.email_verified
            ? t("onboarding.email_verified")
            : t("onboarding.email_pending", { email: s?.masked_email ?? "" })}
        </Text>
      </Stack>
      {s?.code_issued && (
        <Stack>
          <Notice>{t("onboarding.code_sent")}</Notice>
          <SignInChoice onChoose={() => clearOnboarding()} />
        </Stack>
      )}
      {notice && <Notice>{notice}</Notice>}
      <FormError message={error} />
    </Page>
  );
}

/**
 * SignInChoice lets a new member choose how to confirm sign-ins on new
 * devices: an authenticator app, set up right after the first sign-in, or
 * codes by SMS until the administrator's limit (ADR 0039).
 */
function SignInChoice({ onChoose }: { onChoose?: () => void }) {
  const { t } = useTranslation();
  return (
    <Stack gap="sm" data-testid="sign-in-choice">
      <Text fw={600}>{t("onboarding.choice_title")}</Text>
      <Text size="sm">{t("onboarding.choice_body")}</Text>
      <Button
        component={Link}
        to={`/signin?next=${encodeURIComponent(SETUP_AUTHENTICATOR_PATH)}`}
        onClick={onChoose}
        fullWidth
      >
        {t("onboarding.choice_app")}
      </Button>
      <Button component={Link} to="/signin" onClick={onChoose} variant="default" fullWidth>
        {t("onboarding.choice_sms")}
      </Button>
    </Stack>
  );
}
